import type { CertificateViewVariant } from './CertificateModals';
import {
  CertificateLoadingModal,
  CertificateViewModal,
} from './CertificateModals';
import type { PrintableCertificateData } from './PrintableCertificates';
import { PrintableCertificates } from './PrintableCertificates';

export interface PrintCertificateModalsProps {
  isLoadingModalOpen: boolean;
  isViewModalOpen: boolean;
  loadingProgress: number;
  viewModalVariant: CertificateViewVariant;
  certificates: PrintableCertificateData[];
  handlePrint: () => void;
  handleCloseViewModal: () => void;
  handleCancelLoading: () => void;
  handleCertificatesReady: () => void;
  handleCertificatesProgress: (progress: number) => void;
}

/**
 * Renders the certificate modal components (loading, view, and printable certificates).
 * Use alongside `usePrintCertificates` hook when you need custom trigger behavior.
 *
 * Props are designed to spread directly from the hook return value:
 * @example
 * ```tsx
 * const certificateHook = usePrintCertificates({ planId });
 *
 * return (
 *   <>
 *     <Button onClick={certificateHook.handleClick}>Get Certificates</Button>
 *     <PrintCertificateModals {...certificateHook} />
 *   </>
 * );
 * ```
 */
export const PrintCertificateModals: React.FC<PrintCertificateModalsProps> = ({
  isLoadingModalOpen,
  isViewModalOpen,
  loadingProgress,
  viewModalVariant,
  certificates,
  handlePrint,
  handleCloseViewModal,
  handleCancelLoading,
  handleCertificatesReady,
  handleCertificatesProgress,
}) => (
  <>
    <CertificateLoadingModal
      isOpen={isLoadingModalOpen}
      progress={loadingProgress}
      count={certificates.length}
      onCancel={handleCancelLoading}
    />

    <CertificateViewModal
      isOpen={isViewModalOpen}
      onClose={handleCloseViewModal}
      onViewAndSave={handlePrint}
      variant={viewModalVariant}
    />

    {certificates.length > 0 && (
      <PrintableCertificates
        certificates={certificates}
        onReady={handleCertificatesReady}
        onProgress={handleCertificatesProgress}
      />
    )}
  </>
);
